import { createContext, MutableRefObject, ReactNode, useContext } from 'react';

import type { NotificationContextValue } from 'app/notifications/Notification';
import type { Variant } from 'react-bootstrap/esm/types';
import { getAppIntl } from 'i18n/appIntl';

export type ToastButton = {
    iconClass?: string;
    onClick: () => void;
    text: string;
    variant?: 'primary' | 'secondary' | 'outline-primary' | 'outline-secondary' | 'success' | 'danger' | 'warning' | 'info';
};

export type CatchWithToast = {
    (error: any, message: string): void;
    (message: string): (error: any) => void;
};

// Module-level ref to the active GMetrixToast instance. This lets the default
// (pre-provider) context values still pop real toasts instead of only logging.
let activeToastRef: MutableRefObject<any> = null;
export const setActiveToastRef = (ref: MutableRefObject<any>) => activeToastRef = ref;
export const getActiveToastRef = () => activeToastRef;

const fireToast = (message: ReactNode, variant: Variant, closeTimeout?: number, button?: ToastButton) => {

    if (activeToastRef?.current?.createToast) {

        activeToastRef.current.createToast(message, variant, closeTimeout, button);

    } else {

        // Provider not mounted yet; surface to console so the message isn't lost.
        console.warn('[useNotification] Toast fired before provider mounted:', message);

    }

};

/**
 * Translate a raw caught error into a plain-language message a user can act on.
 *
 * The caller passes a context-specific fallback (e.g. 'Failed to load license
 * data.'); this only overrides it when the error matches a well-known category
 * (connectivity, timeout, permission, missing resource, conflict, server fault)
 * that we can phrase more helpfully. Anything else keeps the caller's wording.
 *
 * The original error is never altered here — devs still get the raw object via
 * the console.error in catchWithToast.
 */
const deriveUserFacingMessage = (error: any, fallback: string): string => {

    const { formatMessage } = getAppIntl();

    // axios rejects with these when the request never reached the server.
    if (error?.code === 'ERR_NETWORK' || error?.message === 'Network Error') {

        return formatMessage({ id: 'unable_to_reach_the_server_please_check.e5671c8a', defaultMessage: 'Unable to reach the server. Please check your internet connection and try again.' });

    }
    if (error?.code === 'ECONNABORTED' || /timeout/i.test(String(error?.message ?? ''))) {

        return formatMessage({ id: 'the_request_timed_out_please_try_again.93e77e8d', defaultMessage: 'The request timed out. Please try again.' });

    }

    const status: number | undefined = error?.response?.status ?? error?.status;
    if (status) {

        if (status === 403) return formatMessage({ id: 'you_do_not_have_permission_to_perform_th.f1dd127e', defaultMessage: 'You do not have permission to perform this action.' });
        if (status === 404) return formatMessage({ id: 'the_requested_information_could_not_be_f.e076a64f', defaultMessage: 'The requested information could not be found. It may have been moved or deleted.' });
        if (status === 409) return formatMessage({ id: 'this_change_conflicts_with_the_current_d.42eb4f4a', defaultMessage: 'This change conflicts with the current data. Please refresh and try again.' });
        if (status >= 500) return formatMessage({ id: 'the_server_ran_into_a_problem_please_try.24b3bf62', defaultMessage: 'The server ran into a problem. Please try again in a few moments.' });

    }

    return fallback;

};

/**
 * Build a dual-signature catchWithToast bound to a specific toast emitter.
 *
 *   catchWithToast(error, message)  → logs + toasts immediately (preferred)
 *   catchWithToast(message)(error)  → curried form for `.catch(...)` chains
 */
export const createCatchWithToast = (emitToast: (message: string) => void): CatchWithToast => ((errorOrMessage: any, maybeMessage?: string) => {

    // Backward compatibility: catchWithToast(message)(error)
    if (typeof errorOrMessage === 'string' && maybeMessage === undefined) {

        const message = errorOrMessage;
        return (error: any) => {

            console.error('[catchWithToast]', message, error);
            emitToast(deriveUserFacingMessage(error, message));

        };

    }

    // Preferred style: catchWithToast(error, message)
    const { formatMessage } = getAppIntl();
    const error = errorOrMessage;
    const message = maybeMessage || formatMessage({ id: 'an_unexpected_error_occurred.5e3346f5', defaultMessage: 'An unexpected error occurred.' });
    console.error('[catchWithToast]', message, error);
    emitToast(deriveUserFacingMessage(error, message));

}) as CatchWithToast;

export const NotificationContext = createContext<NotificationContextValue>({
    catchWithToast: createCatchWithToast((message: string) => fireToast(message, 'danger')),
    clearAllToasts: () => {

        activeToastRef?.current?.clearAllToasts?.();

    },
    createToast: (message: ReactNode, variant: Variant, closeTimeout?: number, button?: ToastButton) => {

        fireToast(message, variant, closeTimeout, button);

    },
    onErrorToast: (message: string) => {

        fireToast(message, 'danger');

    },
    onSuccessToast: (message: string) => {

        fireToast(message, 'success');

    }
});

NotificationContext.displayName = 'NotificationContext';

export const useNotification = () => useContext(NotificationContext);
